import { useEffect, useMemo, useRef, useState } from "react";
import { format, startOfDay } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  MapPin,
  Search,
  Users,
} from "lucide-react";
import { HOME } from "@/lib/testIds";
import PropertyCard from "@/components/PropertyCard";
import { useHome } from "@/hooks/useApiData";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

const GUEST_OPTIONS = [1, 2, 3, 4, 5, 6];

const formatGuestLabel = (count) => (count === 6 ? "6+" : String(count));

const stripUnitedStates = (value) =>
  String(value || "").replace(/,\s*United States$/i, "");

const formatLocation = (location) => {
  if (!location) return "";
  if (typeof location === "string") return stripUnitedStates(location);
  if (typeof location === "object") {
    return [location.city, location.state].filter(Boolean).join(", ");
  }
  return "";
};

export const Home = () => {
  const [destination, setDestination] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [dates, setDates] = useState(undefined);
  const [dateOpen, setDateOpen] = useState(false);
  const [guests, setGuests] = useState("");
  const [guestOpen, setGuestOpen] = useState(false);
  const searchFormRef = useRef(null);
  const navigate = useNavigate();
  const { properties, collections, testimonials } = useHome();

  const featured = useMemo(() => properties.slice(0, 3), [properties]);

  const availableLocations = useMemo(
    () =>
      [...new Set(properties.map((p) => formatLocation(p.location)).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [properties],
  );

  const matchingLocations = useMemo(() => {
    const needle = destination === "All" ? "" : destination.trim().toLowerCase();
    return availableLocations.filter(
      (location) => !needle || location.toLowerCase().includes(needle),
    );
  }, [availableLocations, destination]);

  const collectionCovers = useMemo(
    () =>
      collections
        .map((c) => {
          const match = properties.find((p) => p.collection === c.id);
          return { ...c, image: match?.images?.[0] };
        })
        .filter((c) => c.image),
    [collections, properties],
  );

  const testimonial = testimonials[0];
  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    const closeMenus = (event) => {
      if (searchFormRef.current && !searchFormRef.current.contains(event.target)) {
        setLocationOpen(false);
        setDateOpen(false);
        setGuestOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenus);
    return () => document.removeEventListener("mousedown", closeMenus);
  }, []);

  const featuredLayout = (index) => {
    const total = featured.length;

    if (total === 1) return { className: "md:col-span-12", variant: "wide" };
    if (total === 2) return { className: "md:col-span-6", variant: "default" };
    if (total === 3) return { className: "md:col-span-4", variant: "default" };
    if (total === 4) return { className: "md:col-span-6", variant: "default" };
    if (index === 0) return { className: "md:col-span-7", variant: "wide" };
    if (index === 1) return { className: "md:col-span-5", variant: "tall" };

    return { className: "md:col-span-4", variant: "default" };
  };

  const openLocation = () => {
    setLocationOpen(true);
    setDateOpen(false);
    setGuestOpen(false);
  };

  const toggleDate = () => {
    setDateOpen((open) => !open);
    setLocationOpen(false);
    setGuestOpen(false);
  };

  const toggleGuests = () => {
    setGuestOpen((open) => !open);
    setLocationOpen(false);
    setDateOpen(false);
  };

  const onSearch = (event) => {
    event.preventDefault();

    const queryParams = new URLSearchParams();
    const cleanedDestination = destination.trim();
    const guestCount = Number.parseInt(guests, 10);

    if (!cleanedDestination) {
      openLocation();
      return;
    }

    if (!dates?.from || !dates?.to) {
      setLocationOpen(false);
      setGuestOpen(false);
      setDateOpen(true);
      return;
    }

    if (!Number.isFinite(guestCount) || guestCount <= 0) {
      setLocationOpen(false);
      setDateOpen(false);
      setGuestOpen(true);
      return;
    }

    if (cleanedDestination !== "All") queryParams.set("q", cleanedDestination);
    queryParams.set("date", format(dates.from, "yyyy-MM-dd"));
    queryParams.set("check_in", format(dates.from, "yyyy-MM-dd"));
    queryParams.set("check_out", format(dates.to, "yyyy-MM-dd"));
    queryParams.set("guests", String(guestCount));

    navigate(`/properties?${queryParams.toString()}`);
  };

  const dateLabel = dates?.from
    ? dates?.to
      ? `${format(dates.from, "MMM d")} – ${format(dates.to, "MMM d")}`
      : format(dates.from, "MMM d")
    : "WHEN";

  return (
    <main className="overflow-x-hidden">
      <section
        data-testid={HOME.hero}
        className="relative isolate -mt-[68px] min-h-[640px] w-full overflow-hidden text-[#F6F4EF] md:-mt-[76px] md:h-[100svh] md:min-h-[720px]"
      >
        <img
          src={HERO_IMAGE}
          alt="Cinematic villa"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/70" />

        <div className="relative z-10 mx-auto flex min-h-[640px] max-w-[1400px] flex-col items-center justify-center px-5 py-24 text-center md:h-full md:min-h-0 md:px-10 md:py-28">
          <h1
            data-testid={HOME.heroHeadline}
            className="font-display text-[4rem] font-medium leading-none tracking-tight reveal-up"
            style={{ animationDelay: "80ms" }}
          >
            <span className="italic">Explore Properties</span>
          </h1>

          <form
            ref={searchFormRef}
            onSubmit={onSearch}
            className="relative mt-8 grid w-full max-w-full grid-cols-1 items-stretch overflow-visible rounded-3xl border border-white/20 bg-[#F6F4EF] text-left text-[#0F0F0F] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] backdrop-blur-md md:mt-12 md:max-w-4xl md:grid-cols-[1.15fr_1fr_0.75fr_auto] md:rounded-3xl reveal-up"
            style={{ animationDelay: "240ms" }}
          >
            <div
              role="button"
              tabIndex={0}
              aria-haspopup="listbox"
              aria-expanded={locationOpen}
              onClick={(event) => {
                if (event.target.closest("[data-search-popover]")) return;
                openLocation();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openLocation();
                }
              }}
              className="relative flex min-w-0 cursor-pointer items-center gap-3 border-b border-black/10 px-5 py-3.5 md:border-b-0 md:border-r md:px-6 md:py-4"
            >
              <MapPin size={16} className="shrink-0 text-primary" />
              <span
                data-testid={HOME.searchDestination}
                aria-label="Where"
                className={`home-search-control min-w-0 flex-1 truncate font-medium tracking-[0.12em] ${destination ? "text-[#0F0F0F]" : "text-black"}`}
              >
                {destination || "WHERE"}
              </span>
              <ChevronDown
                size={15}
                className={`shrink-0 text-black/55 transition-transform ${
                  locationOpen ? "rotate-180" : ""
                }`}
              />

              {locationOpen && (
                <div
                  data-search-popover
                  role="listbox"
                  onClick={(event) => event.stopPropagation()}
                  className="home-search-dropdown home-search-dropdown-location absolute left-0 right-0 top-[calc(100%+10px)] z-50 w-full max-w-[calc(100vw-2.5rem)]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setDestination("All");
                      setLocationOpen(false);
                    }}
                    className={`block w-full rounded-xl px-4 py-3 text-left text-[13px] font-medium transition-colors ${
                      destination === "All"
                        ? "bg-primary text-primary-foreground"
                        : "text-[#0F0F0F] hover:bg-black/[0.06]"
                    }`}
                  >
                    All
                  </button>
                  {matchingLocations.map((location) => (
                    <button
                      key={location}
                      type="button"
                      onClick={() => {
                        setDestination(location);
                        setLocationOpen(false);
                      }}
                      className={`block w-full rounded-xl px-4 py-3 text-left text-[13px] font-medium transition-colors ${
                        destination === location
                          ? "bg-primary text-primary-foreground"
                          : "text-[#0F0F0F] hover:bg-black/[0.06]"
                      }`}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div
              role="button"
              tabIndex={0}
              aria-required="true"
              onClick={(event) => {
                if (event.target.closest("[data-search-popover]")) return;
                toggleDate();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleDate();
                }
              }}
              className="relative flex min-w-0 cursor-pointer items-center gap-3 border-b border-black/10 px-5 py-3.5 md:border-b-0 md:border-r md:px-6 md:py-4"
            >
              <CalendarDays size={16} className="shrink-0 text-primary" />
              <span
                data-testid={HOME.searchDates}
                className={`home-search-control min-w-0 flex-1 truncate text-left font-medium uppercase tracking-[0.12em] ${
                  dates?.from ? "text-[#0F0F0F]" : "text-black"
                }`}
              >
                {dateLabel}
              </span>
              <ChevronDown
                size={15}
                className={`shrink-0 text-black/55 transition-transform ${
                  dateOpen ? "rotate-180" : ""
                }`}
              />

              {dateOpen && (
                <div
                  data-search-popover
                  onClick={(event) => event.stopPropagation()}
                  className="home-search-calendar-popover absolute left-0 top-[calc(100%+10px)] z-50 w-full max-w-[calc(100vw-2.5rem)]"
                >
                  <DayPicker
                    mode="range"
                    selected={dates}
                    onSelect={(range) => {
                      setDates(range);
                      if (range?.from && range?.to) setDateOpen(false);
                    }}
                    disabled={{ before: today }}
                    fromDate={today}
                    numberOfMonths={1}
                    showOutsideDays
                    fixedWeeks
                    className="home-hero-calendar"
                  />
                </div>
              )}
            </div>

            <div
              role="button"
              tabIndex={0}
              aria-required="true"
              onClick={(event) => {
                if (event.target.closest("[data-search-popover]")) return;
                toggleGuests();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleGuests();
                }
              }}
              className="relative flex min-w-0 cursor-pointer items-center gap-3 border-b border-black/10 px-5 py-3.5 md:border-b-0 md:px-6 md:py-4"
            >
              <Users size={16} className="shrink-0 text-primary" />
              <span
                data-testid={HOME.searchGuests}
                className="home-search-control min-w-0 flex-1 truncate text-left font-medium uppercase tracking-[0.12em] text-black"
              >
                {guests ? formatGuestLabel(Number(guests)) : "GUESTS"}
              </span>
              <ChevronDown
                size={15}
                className={`shrink-0 text-black/55 transition-transform ${
                  guestOpen ? "rotate-180" : ""
                }`}
              />

              {guestOpen && (
                <div
                  data-search-popover
                  onClick={(event) => event.stopPropagation()}
                  className="home-search-dropdown home-search-dropdown-guests absolute left-0 right-0 top-[calc(100%+10px)] z-50 w-full max-w-[calc(100vw-2.5rem)]"
                >
                  {GUEST_OPTIONS.map((count) => (
                    <button
                      key={count}
                      type="button"
                      onClick={() => {
                        setGuests(String(count));
                        setGuestOpen(false);
                      }}
                      className={`block w-full rounded-xl px-4 py-3 text-left text-[12px] font-medium transition-colors ${
                        guests === String(count)
                          ? "bg-primary text-primary-foreground"
                          : "text-[#0F0F0F] hover:bg-black/[0.06]"
                      }`}
                    >
                      {formatGuestLabel(count)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              data-testid={HOME.searchSubmit}
              className="m-3 inline-flex min-w-0 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 md:m-2 md:py-3"
            >
              <Search size={16} /> Search
            </button>
          </form>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <div className="mb-8 flex items-end justify-between md:mb-12">
            <div>
              <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:mb-4 md:text-xs">
                Featured · Nº 01
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.95] tracking-tight md:text-6xl md:leading-none">
                Places worth
                <br />
                staying <span className="italic">longer</span>.
              </h2>
            </div>
            <Link
              to="/properties"
              className="hidden items-center gap-2 text-sm font-medium text-foreground transition-transform duration-300 hover:translate-x-1 md:inline-flex"
            >
              All stays <ArrowRight size={16} />
            </Link>
          </div>

          <div
            data-testid={HOME.featuredGrid}
            className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-12 md:gap-x-8 md:gap-y-12"
          >
            {featured.map((property, index) => {
              const layout = featuredLayout(index);

              return (
                <div key={property.id} className={layout.className}>
                  <PropertyCard property={property} variant={layout.variant} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {collectionCovers.length > 0 && (
        <section className="border-y border-foreground/10 bg-muted/40 py-16 md:py-20">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="mb-8 flex items-end justify-between md:mb-10">
              <div>
                <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:mb-4 md:text-xs">
                  Curated · Nº 02
                </p>
                <h2 className="font-display text-4xl font-medium leading-[0.95] tracking-tight md:text-6xl md:leading-none">
                  By mood.
                </h2>
              </div>
            </div>

            <div
              data-testid={HOME.collectionsScroller}
              className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 md:-mx-10 md:gap-6 md:px-10"
            >
              {collectionCovers.map((c) => (
                <Link
                  key={c.id}
                  to={`/properties?collection=${c.id}`}
                  className="group relative aspect-[3/4] w-[75vw] shrink-0 snap-start overflow-hidden bg-muted sm:w-[42vw] md:w-[26vw]"
                >
                  <img
                    src={c.image}
                    alt={c.label}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-background">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-background/70">
                      {c.count} stays
                    </p>
                    <h3 className="mt-1 font-display text-3xl font-medium leading-tight tracking-tight">
                      {c.label}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {testimonial?.quote && (
        <section
          data-testid={HOME.testimonial}
          className="mx-auto max-w-[1200px] px-5 py-20 text-center md:px-10 md:py-28"
        >
          <p className="mb-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:mb-6 md:text-xs">
            A note · Nº 03
          </p>
          <blockquote className="font-display text-3xl font-medium leading-[1.15] tracking-tight text-balance md:text-6xl md:leading-[1.1]">
            <span className="text-primary">“</span>
            {testimonial.quote}
            <span className="text-primary">”</span>
          </blockquote>
          {(testimonial.author || testimonial.role) && (
            <div className="mx-auto mt-8 flex items-center justify-center gap-3 md:mt-10 md:gap-4">
              <div className="h-px w-8 bg-foreground/30 md:w-12" />
              <p className="text-xs text-muted-foreground md:text-sm">
                {testimonial.author}
                {testimonial.author && testimonial.role ? " — " : ""}
                {testimonial.role}
              </p>
              <div className="h-px w-8 bg-foreground/30 md:w-12" />
            </div>
          )}
        </section>
      )}

      <section
        data-testid={HOME.hostBanner}
        className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24"
      >
        <div className="relative overflow-hidden bg-[#2E4034] text-[#F6F4EF]">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="p-8 md:p-16 lg:p-20">
              <p className="mb-4 text-[10px] uppercase tracking-[0.2em] text-[#F6F4EF]/70 md:mb-6 md:text-xs">
                For hosts
              </p>
              <h3 className="font-display text-4xl font-medium leading-[0.95] tracking-tight md:text-7xl">
                Turn your
                <br />
                <span className="italic">favorite room</span>
                <br />
                into income.
              </h3>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-[#F6F4EF]/80 md:mt-8 md:text-base">
                Hosts on StayEasy earn an average of $2,340 per month. We handle
                the details, you keep the good taste.
              </p>
              <Link
                to="/hosts/subscription"
                data-testid={HOME.hostBannerCta}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 md:mt-10 md:px-7 md:py-3.5"
              >
                Start hosting <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative min-h-[240px] md:min-h-[400px]">
              <img
                src="https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
                alt="Host home"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;